import { type ReactNode } from 'react';

import { ThemeProvider, type Theme } from '@mui/material/styles';

/**
 * The theme, adjusted for the people this claim is for. Proposed for the design
 * system, and scoped here until that decision is made, so nothing else in Loft
 * changes under anyone.
 *
 * Measured on 29 September with axe:
 * - Secondary text (descriptions, hints, captions, overlines) was neutral 500
 *   on the cream background, 3.1:1. Neutral 600 is 5.2:1.
 * - The family's own chat bubble and voice note were white on coral 400,
 *   2.6:1, and coral 600 still measured 4.3:1. Coral 700 passes.
 * - Button labels had a fixed 24px line height, so at larger text sizes a
 *   wrapped label drew its lines over each other. A ratio grows with the text.
 *
 * The typography variants carry a literal colour baked in by llama, so they
 * are re-coloured here as well as the palette.
 */
const adjust = (outer: Theme): Theme => {
  const secondary = outer.palette.custom.neutral[600];
  const recolour = (variant: keyof Theme['typography']) => ({
    ...(outer.typography[variant] as object),
    color: secondary,
  });
  return {
    ...outer,
    palette: {
      ...outer.palette,
      text: { ...outer.palette.text, secondary },
      custom: {
        ...outer.palette.custom,
        coral: {
          ...outer.palette.custom.coral,
          400: outer.palette.custom.coral[700],
        },
      },
    },
    typography: {
      ...outer.typography,
      subtitle1: recolour('subtitle1'),
      subtitle2: recolour('subtitle2'),
      caption: recolour('caption'),
      overline: recolour('overline'),
      button: { ...outer.typography.button, lineHeight: 1.3 },
    },
  } as Theme;
};

const ClaimTheme = ({ children }: { children?: ReactNode }) => (
  <ThemeProvider theme={adjust}>{children}</ThemeProvider>
);

export default ClaimTheme;
